import React, { useState, useMemo, useCallback } from 'react';
import DropdownMenuStateless, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Field } from '@atlaskit/form';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import { LabelWrapper } from '../../../../common/ui/label/index.tsx';
import { DROPDOWN_OPTIONS } from './constants';
import messages from './messages';
import type { TimeBreakdown, DropdownOption, Item, AvailableOptions } from './types';
import { getSelectedOptionByTimeBreakdown } from './utils';

type Props = {
	loading: boolean;
	selectedTimeBreakdownId?: TimeBreakdown;
	availableTimeBreakdownOptions: AvailableOptions;
	onTimeBreakdownSelect: (arg1: TimeBreakdown) => void;
	intl: IntlShape;
};

const TimeBreakdownDropdown = ({
	intl,
	loading,
	selectedTimeBreakdownId,
	availableTimeBreakdownOptions,
	onTimeBreakdownSelect,
}: Props) => {
	const [open, setOpen] = useState(false);

	const onDropdownSelect = useCallback(
		({ id }: DropdownOption) => {
			onTimeBreakdownSelect(id);
		},
		[onTimeBreakdownSelect],
	);

	const toggleDropdown = () => setOpen((isOpen) => !isOpen);

	const dropdownOptions = useMemo(
		(): DropdownOption[] =>
			DROPDOWN_OPTIONS.map(({ id, message }: Item) => ({
				id,
				message: intl.formatMessage(message),
			})),
		[intl],
	);

	const selectedOption = useMemo(
		(): DropdownOption =>
			getSelectedOptionByTimeBreakdown(dropdownOptions, selectedTimeBreakdownId),
		[dropdownOptions, selectedTimeBreakdownId],
	);

	const dropdownItems = useMemo(() => {
		const { validOptions } = availableTimeBreakdownOptions;
		return dropdownOptions.map((item: DropdownOption) =>
			item.id !== selectedOption.id ? (
				<DropdownItem
					key={item.id}
					onClick={() => onDropdownSelect(item)}
					isDisabled={loading || !validOptions.includes(item.id)}
				>
					{item.message}
				</DropdownItem>
			) : null,
		);
	}, [availableTimeBreakdownOptions, dropdownOptions, loading, onDropdownSelect, selectedOption]);

	return (
		<div data-test-id="servicedesk-reports-common.ui.date-filter.date-picker-group.time-breakdown.time-breakdown-picker">
			<LabelWrapper>
				<Field name="period" label={intl.formatMessage(messages.period)}>
					{() => (
						<div>
							<DropdownMenuStateless
								trigger={selectedOption.message}
								isOpen={open}
								onOpenChange={toggleDropdown}
							>
								<DropdownItemGroup>{dropdownItems}</DropdownItemGroup>
							</DropdownMenuStateless>
						</div>
					)}
				</Field>
			</LabelWrapper>
		</div>
	);
};

export default injectIntl(TimeBreakdownDropdown);
